import clsx from 'clsx';
import type { InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
    readonly className?: string;
    readonly disabled?: boolean;
    readonly placeholder?: string;
    readonly variant?: 'default' | 'error' | 'success';
};

const variantBorderClasses = {
    default: 'border-gray-300',
    error: 'border-error-400',
    success: 'border-success-400',
} as const;

const variantFocusClasses = {
    default: 'hover:border-gray-700 focus:border-gray-700',
    error: 'hover:border-error-600 focus:border-error-600',
    success: 'hover:border-success-600 focus:border-success-600',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    { className = '', disabled = false, placeholder, variant = 'default', ...props },
    ref,
) {
    return (
        <input
            ref={ref}
            className={clsx(
                'w-full box-border bg-white-200 text-gray-900 rounded-md outline-none',
                'border px-l py-m',
                'text-xxs leading-[1rem] font-sans font-normal tracking-tight',
                'placeholder:text-gray-700',
                'file:bg-transparent file:border-none file:rounded-none file:text-inherit file:cursor-pointer file:font-inherit file:m-0 file:p-0 file:pr-l',
                'transition-all duration-100 ease-in-out',
                variantBorderClasses[variant],
                disabled ? 'cursor-default opacity-50' : clsx('cursor-text', variantFocusClasses[variant]),
                className,
            )}
            disabled={disabled}
            placeholder={placeholder}
            {...props}
        />
    );
});
